/**
 * 자동 후속 턴 — 비동기 전환의 **완화 설계**(B §7 · B-D1).
 *
 * 동기 시절에는 저작 결과를 같은 턴에서 인용했다. 비동기가 되면 그 인용이 사라지는데, 그냥
 * 두면 사용자는 "완료" 알림만 받고 무엇이 나왔는지는 직접 열어봐야 한다. 그래서 job 이
 * `complete` 로 바뀌는 순간 **프런트가 후속 발화를 대신 던져** 에이전트가 저장된 결과를 읽고
 * 이어 말하게 한다.
 *
 * ⚠ 이 설계의 품질은 **B-6 블라인드 비교**가 판정한다(사전 등록서: 통과선 4차원 격차 ≤0.5,
 * `continuity` 단독 ≤1.0). 그 비교의 B 항을 만드는 것이 바로 이 함수다.
 *
 * 규율 넷 — 전부 "자동"이 사용자를 놀라게 하지 않기 위한 것이다:
 *  ① **job 당 1회**. 폴링이 같은 complete 를 여러 번 보므로 id 로 막지 않으면 후속 턴이 쌓인다.
 *  ② **스트림 중에는 안 던진다**. 사용자가 말하는 도중에 끼어들면 대화가 엉킨다.
 *  ③ **탭이 숨겨져 있으면 보류**. 자리에 없는 사람에게 대화를 진행시키지 않는다(B §7 "부재 시
 *     알림으로 강등"). 돌아오면 그때 던진다.
 *  ④ **실패·취소에는 던지지 않는다**. 실패 사유는 알림으로 충분하고, 에이전트에게 "실패를
 *     설명하라"고 시키면 없는 원인을 지어낼 여지를 준다.
 */
import type { JobView } from './types'

/**
 * 후속 발화 문구 — 에이전트가 **저장된 결과를 읽어** 말하도록 지시한다(값을 지어내지 않게).
 *
 * ⚠ 도구 이름을 문구에 박는다(W2). 그냥 "확인해 주세요"라고만 하면 에이전트가 확인 수단으로
 * `author_masterplan` 을 고를 수 있고, 저작이 **끝난 뒤** 그 도구는 접수증이 아니라
 * **새 저작(8~9분·상당한 비용)** 을 만든다. 읽기 도구를 지목하는 한 문장이 그 경로를 닫는다.
 */
export const FOLLOW_UP_PROMPT =
  '방금 마스터플랜 저작이 끝났습니다. `get_masterplan_result` 로 저장된 결과를 읽어서 ' +
  '무엇이 준비됐는지, 검토가 필요한 부분(가정·경고)이 있으면 함께 정리해 주세요. ' +
  '다시 저작하지는 마세요.'

export interface FollowUpContext {
  job: JobView | null | undefined
  /** 이미 후속 턴을 던진 job id 들. */
  done: ReadonlySet<string>
  /** 지금 스트림이 돌고 있는가. */
  streaming: boolean
  /** 탭이 보이는가(`document.visibilityState === 'visible'`). */
  visible: boolean
}

export type FollowUpDecision =
  | { fire: false; reason: 'no-job' | 'not-complete' | 'already-done' | 'streaming' | 'hidden' }
  | { fire: true; jobId: string; prompt: string }

/** 순수 판정 — 브라우저 없이 테스트된다(이 결정이 잘못되면 대화가 오염된다). */
export function decideFollowUp(ctx: FollowUpContext): FollowUpDecision {
  const { job } = ctx
  if (!job) return { fire: false, reason: 'no-job' }
  if (job.status !== 'complete') return { fire: false, reason: 'not-complete' }
  if (ctx.done.has(job.job_id)) return { fire: false, reason: 'already-done' }
  if (ctx.streaming) return { fire: false, reason: 'streaming' }
  if (!ctx.visible) return { fire: false, reason: 'hidden' }
  return { fire: true, jobId: job.job_id, prompt: FOLLOW_UP_PROMPT }
}
